import { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  adminAnalyzeImage,
  adminCreateGallery,
  adminUploadImage,
  adminWaitForUploadReady,
  adminUpscaleGallery,
  adminKitOptions,
  adminDetectKitUpload,
  adminVideoMetadata,
  resolveMedia,
} from "@/lib/api";
import { toast } from "sonner";
import { autoResizeImage, formatMB } from "@/lib/imageResize";
import { UploadCloud, Sparkles, ArrowLeft, X, RefreshCw, Plane, Camera, Download } from "lucide-react";
import ShotDetailsEditor from "@/components/admin/ShotDetailsEditor";

const CATEGORIES = [
  "Drone",
  "Roadworks",
  "Structures",
  "People",
  "Night Works",
  "Depots",
  "Plant",
  "Traffic Management",
  "Events",
  "General",
];
const SIZES = ["standard", "portrait_tall", "landscape_wide", "large_feature"];
const VIDEO_KINDS = ["mp4", "vimeo", "youtube"];

const ChipRow = ({ items, value, onChange, testidPrefix }) => (
  <div className="flex flex-wrap gap-2">
    {items.map((it) => (
      <button
        key={it}
        type="button"
        onClick={() => onChange(it)}
        data-testid={`${testidPrefix}-${it.toLowerCase().replace(/\s+/g, "-")}`}
        className={`hl-chip ${value === it ? "active" : ""}`}
      >
        {it.replace(/_/g, " ")}
      </button>
    ))}
  </div>
);

export default function AdminGalleryUpload() {
  const navigate = useNavigate();
  const fileInput = useRef(null);

  const [mode, setMode] = useState("photo"); // photo | video
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploadedPath, setUploadedPath] = useState(null);

  // Admin-entered (required) inputs
  const [originalName, setOriginalName] = useState("");
  const [clientName, setClientName] = useState("");

  const [analysing, setAnalysing] = useState(false);
  const [aiDone, setAiDone] = useState(false);
  const [saving, setSaving] = useState(false);
  const [processingStage, setProcessingStage] = useState(""); // "" | "uploading" | "processing" | "ready" | "failed"
  const [detected, setDetected] = useState([]);
  const [technical, setTechnical] = useState(null);
  const [upscaleAfter, setUpscaleAfter] = useState(false);

  // AI / form output (editable by admin before publish)
  const [form, setForm] = useState({
    title: "",
    category: "General",
    size: "standard",
    description: "",
    alt_text: "",
    featured: false,
    video: "",
    video_kind: "vimeo",
    kit_used: "DJI Mavic 4 Pro",
  });
  const [kitOptions, setKitOptions] = useState({ cameras: [], drones: [] });
  const [detectingKit, setDetectingKit] = useState(false);
  const [videoMeta, setVideoMeta] = useState(null);
  const [fetchingMeta, setFetchingMeta] = useState(false);
  const [preparingFile, setPreparingFile] = useState(false);

  const fetchVideoMeta = async () => {
    if (!form.video.trim()) {
      toast.error("Paste a YouTube or Vimeo URL first.");
      return;
    }
    setFetchingMeta(true);
    try {
      const res = await adminVideoMetadata(form.video.trim());
      setVideoMeta(res);
      if (res.image) {
        setFile(null);
        setPreview(resolveMedia(res.image));
        setUploadedPath(res.path || null);
      }
      setForm((f) => ({
        ...f,
        video_kind: res.video_kind || f.video_kind,
        title: res.title || f.title,
        description: res.description || f.description,
        alt_text: f.alt_text || (res.title ? `Video still - ${res.title}` : f.alt_text),
      }));
      toast.success(`Fetched from ${res.video_kind === "vimeo" ? "Vimeo" : "YouTube"} - thumbnail and details applied.`);
    } catch (e) {
      const detail = e?.response?.data?.error || e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't fetch video details.");
    } finally {
      setFetchingMeta(false);
    }
  };

  useEffect(() => {
    adminKitOptions().then((k) => setKitOptions({ cameras: k.cameras || [], drones: k.drones || [] })).catch(() => {});
  }, []);

  const detectKitFromFile = async () => {
    if (!file) {
      toast.error("Choose a photo first.");
      return;
    }
    setDetectingKit(true);
    try {
      const res = await adminDetectKitUpload(file);
      setForm((f) => ({ ...f, kit_used: res.kit_used }));
      toast.success(`AI says: ${res.kit_used} (${res.is_drone ? "drone" : "ground"} shot)`);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Kit detection failed.");
    } finally {
      setDetectingKit(false);
    }
  };

  const onPickFile = async (f) => {
    if (!f) return;
    setPreparingFile(true);
    if (f.size > 10 * 1024 * 1024) {
      toast.message("Image is over the size limit, auto-resizing to fit…", { id: "auto-resize" });
    }
    try {
      const { file: fitted, resized } = await autoResizeImage(f);
      if (resized) {
        toast.success(`Auto-resized to fit (${formatMB(f.size)} → ${formatMB(fitted.size)}).`, { id: "auto-resize" });
      } else {
        toast.dismiss("auto-resize");
      }
      f = fitted;
    } catch {
      toast.dismiss("auto-resize");
    }
    setFile(f);
    setPreview(URL.createObjectURL(f));
    setUploadedPath(null);
    setAiDone(false);
    setTechnical(null);
    setPreparingFile(false);
  };

  const onDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    if (f) onPickFile(f);
  };

  const clearFile = () => {
    setFile(null);
    setPreview(null);
    setUploadedPath(null);
    setAiDone(false);
    setDetected([]);
    setTechnical(null);
    setProcessingStage("");
  };

  const canAnalyse = !!file && originalName.trim().length > 0 && !analysing;

  const analyse = async ({ remix = false } = {}) => {
    if (!file) {
      toast.error(mode === "video" ? "Upload a poster image first." : "Choose an image first.");
      return;
    }
    if (!originalName.trim()) {
      toast.error(mode === "video" ? "Please enter the original video name first." : "Please enter the original photo name first.");
      return;
    }
    setAnalysing(true);
    try {
      const data = await adminAnalyzeImage(file, {
        original_name: originalName,
        client: clientName,
        media_type: mode,
        remix,
      });
      setForm((prev) => ({
        ...prev,
        title: data.title || prev.title,
        category: data.category || prev.category,
        size: data.suggested_size || prev.size,
        description: data.description || prev.description,
        alt_text: data.alt_text || prev.alt_text,
      }));
      setDetected(data.detected || []);
      if (data.technical) setTechnical(data.technical);
      setAiDone(true);
      toast.success(remix ? "Remix ready, fresh wording applied." : "AI suggestions ready, review and edit before publishing.");
    } catch (err) {
      const data = err?.response?.data || {};
      const detail = data.error || data.detail || err?.message || "AI analysis failed.";
      toast.error(typeof detail === "string" ? detail : "AI analysis failed.");
    } finally {
      setAnalysing(false);
    }
  };

  const save = async (asDraft) => {
    if (mode === "photo" && !file) {
      toast.error("Please upload an image first.");
      return;
    }
    if (mode === "video" && !form.video) {
      toast.error("Please paste a video URL (Vimeo, YouTube) or upload an MP4.");
      return;
    }
    if (!form.title.trim()) {
      toast.error("Please give the item a title (or run the AI to suggest one).");
      return;
    }
    if (!originalName.trim()) {
      toast.error("Please enter the original photo name.");
      return;
    }

    setSaving(true);
    try {
      let imageUrl = uploadedPath ? `/api/files/${uploadedPath}` : "";
      let resolvedTechnical = technical;
      // Auto-fetch YouTube/Vimeo thumbnail when the admin didn't upload a poster.
      let vmeta = videoMeta;
      if (mode === "video" && form.video_kind !== "mp4" && !file && !uploadedPath && !vmeta) {
        try {
          toast.message("Fetching thumbnail from video…", { id: "video-meta" });
          vmeta = await adminVideoMetadata(form.video.trim());
          setVideoMeta(vmeta);
          toast.success("Thumbnail fetched from video.", { id: "video-meta" });
        } catch (e) {
          toast.dismiss("video-meta");
        }
      }
      if (!imageUrl && mode === "video" && vmeta?.image) imageUrl = vmeta.image;
      if (file && !uploadedPath) {
        // Fast-path upload - saves original quickly, then we poll for variants + EXIF.
        setProcessingStage("uploading");
        let uploaded;
        try {
          uploaded = await adminUploadImage(file);
        } catch (uerr) {
          const status = uerr?.response?.status;
          const data = uerr?.response?.data;
          const msg = (data && (data.error || data.detail)) || "Image upload failed.";
          if (status === 503) {
            toast.error(typeof msg === "string" ? msg : "Storage temporarily unavailable, please try again.");
          } else {
            toast.error(typeof msg === "string" ? msg : "Image upload failed.");
          }
          setProcessingStage("failed");
          setSaving(false);
          return;
        }
        if (!uploaded?.success) {
          toast.error(uploaded?.error || "Image upload failed.");
          setProcessingStage("failed");
          setSaving(false);
          return;
        }
        imageUrl = uploaded.url;
        setUploadedPath(uploaded.path);
        // Poll for variants + EXIF (thumbnail/WebP/technical) - usually 1-3 seconds.
        setProcessingStage("processing");
        toast.message("Image saved · processing details…", { id: "upload-processing" });
        try {
          const ready = await adminWaitForUploadReady(uploaded.imageId, { intervalMs: 1500, timeoutMs: 30000 });
          if (ready.status === "ready") {
            if (!resolvedTechnical && ready.technical) {
              resolvedTechnical = ready.technical;
              setTechnical(ready.technical);
            }
            setProcessingStage("ready");
            toast.success("Image ready.", { id: "upload-processing" });
          } else if (ready.status === "failed") {
            setProcessingStage("failed");
            toast.warning("Variants failed but original is saved. You can still publish.", { id: "upload-processing" });
          } else {
            // timeout - non-fatal, original is uploaded
            setProcessingStage("processing");
            toast.message("Still processing in background, you can still publish.", { id: "upload-processing" });
          }
        } catch (perr) {
          setProcessingStage("processing");
          // status poll failure is non-fatal; original is uploaded
        }
      }

      const payload = {
        type: mode,
        title: form.title.trim(),
        original_name: originalName.trim(),
        client: clientName.trim(),
        category: mode === "video" ? "Video" : form.category,
        size: form.size,
        image: imageUrl,
        thumbnail: mode === "video" && !file ? vmeta?.thumbnail || null : null,
        webp_image: mode === "video" && !file ? vmeta?.webp_image || null : null,
        webp_thumbnail: mode === "video" && !file ? vmeta?.webp_thumbnail || null : null,
        shot_type: mode === "video" ? vmeta?.shot_type || null : null,
        video: mode === "video" ? form.video.trim() : null,
        video_kind: mode === "video" ? form.video_kind : null,
        description: form.description.trim(),
        alt_text: form.alt_text.trim(),
        status: asDraft ? "draft" : "published",
        featured: form.featured,
        technical: mode === "photo"
          ? { ...(resolvedTechnical || {}), kit_used: resolvedTechnical?.kit_used || form.kit_used || "DJI Mavic 4 Pro" }
          : null,
      };
      const created = await adminCreateGallery(payload);
      toast.success(asDraft ? "Saved as draft." : "Published.");

      // Optional: run AI upscale on save (works for both draft and published)
      if (mode === "photo" && upscaleAfter && created?.id) {
        toast.loading("Upscaling… 20-60s", { id: "upscale-after" });
        try {
          await adminUpscaleGallery(created.id, 2);
          toast.success("Upscaled to ultra-HD.", { id: "upscale-after" });
        } catch (uperr) {
          const detail = uperr?.response?.data?.detail;
          toast.error(
            typeof detail === "string" ? detail : "Upscale failed (item still saved).",
            { id: "upscale-after" }
          );
        }
      }

      navigate("/admin/gallery");
    } catch (err) {
      const detail = err?.response?.data?.detail || "Save failed.";
      toast.error(typeof detail === "string" ? detail : "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="px-6 lg:px-10 py-10 max-w-5xl">
      <Link
        to="/admin/gallery"
        className="inline-flex items-center gap-2 text-[11px] tracking-[0.24em] uppercase text-[#A3A3A3] hover:text-white mb-6"
      >
        <ArrowLeft size={14} /> Back to gallery
      </Link>

      <div className="text-[10px] tracking-[0.32em] uppercase text-[#4A6D8C] mb-2">
        New item
      </div>
      <h1 className="font-display text-4xl tracking-tight">Upload to gallery</h1>
      <p className="text-[13px] text-[#A3A3A3] mt-3 max-w-2xl">
        Drop the image, enter the original photo name and client, then let AI suggest the title,
        category, description and alt text. Review and edit everything before you publish.
      </p>

      {/* Mode toggle */}
      <div className="flex gap-2 mt-8" data-testid="mode-toggle">
        <button
          type="button"
          onClick={() => setMode("photo")}
          data-testid="mode-photo"
          className={`hl-chip ${mode === "photo" ? "active" : ""}`}
        >
          Photo
        </button>
        <button
          type="button"
          onClick={() => setMode("video")}
          data-testid="mode-video"
          className={`hl-chip ${mode === "video" ? "active" : ""}`}
        >
          Video
        </button>
      </div>

      <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
        {/* LEFT - Drop zone */}
        <div className="lg:col-span-5">
          {!preview ? (
            <div
              onClick={() => fileInput.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={onDrop}
              data-testid="dropzone"
              className="border border-dashed border-[#404040] hover:border-[#4A6D8C] transition-colors aspect-[4/5] flex flex-col items-center justify-center gap-3 cursor-pointer bg-[#0a0a0a] text-center px-6"
            >
              <UploadCloud size={28} className="text-[#658CAE]" />
              <div className="font-display text-lg">Drop image here</div>
              <div className="text-[12px] text-[#A3A3A3]">
                or click to browse - JPG / PNG / WEBP. Oversized images are auto-resized to fit.
              </div>
              <input
                ref={fileInput}
                type="file"
                accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
                hidden
                data-testid="file-input"
                onChange={(e) => onPickFile(e.target.files?.[0])}
              />
            </div>
          ) : (
            <div
              className="relative bg-[#0a0a0a] overflow-hidden border border-[#1a1a1a] flex items-center justify-center"
              style={{ minHeight: 320, maxHeight: "70vh" }}
              data-testid="preview"
            >
              <img
                src={preview}
                alt="preview"
                className="block w-full h-auto max-h-[70vh] object-contain"
              />
              {processingStage && processingStage !== "ready" && (
                <div
                  className="absolute top-3 left-3 bg-black/70 border border-white/15 backdrop-blur-md px-3 py-1.5 text-[10px] tracking-[0.24em] uppercase flex items-center gap-2"
                  data-testid="upload-status-badge"
                >
                  {processingStage === "uploading" && (
                    <><RefreshCw size={10} className="animate-spin text-[#4A6D8C]" /> <span>Uploading…</span></>
                  )}
                  {processingStage === "processing" && (
                    <><RefreshCw size={10} className="animate-spin text-[#4A6D8C]" /> <span>Processing details…</span></>
                  )}
                  {processingStage === "failed" && (
                    <span className="text-[#ff6b6b]">Processing failed - original saved</span>
                  )}
                </div>
              )}
              {processingStage === "ready" && (
                <div
                  className="absolute top-3 left-3 bg-black/70 border border-[#4A6D8C]/40 backdrop-blur-md px-3 py-1.5 text-[10px] tracking-[0.24em] uppercase text-[#a4c0d8]"
                  data-testid="upload-status-badge"
                >
                  Ready
                </div>
              )}
              <button
                onClick={clearFile}
                data-testid="preview-clear"
                className="absolute top-3 right-3 bg-black/60 border border-white/15 backdrop-blur-md p-2 text-white"
                aria-label="Clear"
              >
                <X size={14} />
              </button>
            </div>
          )}

          {detected.length > 0 && (
            <div className="mt-4">
              <div className="text-[10px] tracking-[0.28em] uppercase text-[#737373] mb-2">
                Detected in image
              </div>
              <div className="flex flex-wrap gap-1.5" data-testid="detected-tags">
                {detected.map((d) => (
                  <span
                    key={d}
                    className="text-[10px] tracking-[0.14em] uppercase border border-[#262626] px-2 py-1 text-[#A3A3A3]"
                  >
                    {d}
                  </span>
                ))}
              </div>
            </div>
          )}

          {mode === "photo" && technical && (
            <div className="mt-6">
              <ShotDetailsEditor
                technical={technical}
                onChange={setTechnical}
                testidPrefix="up-tech"
              />
            </div>
          )}
        </div>

        {/* RIGHT - Form */}
        <div className="lg:col-span-7 flex flex-col gap-1">
          {/* Admin-entered required inputs */}
          <div className="bg-[#0f0f0f] border border-[#1a1a1a] p-5 mb-2">
            <div className="text-[10px] tracking-[0.32em] uppercase text-[#4A6D8C] mb-3">
              1 · Your inputs
            </div>

            <label className="text-[10px] tracking-[0.28em] uppercase text-[#737373]">
              Original photo name <span className="text-[#658CAE]">*</span>
            </label>
            <input
              type="text"
              value={originalName}
              onChange={(e) => setOriginalName(e.target.value)}
              placeholder="e.g. cones at night, drone depot, bridge job"
              className="hl-input"
              data-testid="form-original-name"
            />
            <div className="text-[11px] text-[#525252] mt-1">
              A rough label - the AI uses this plus the image to craft a better title.
            </div>

            <label className="text-[10px] tracking-[0.28em] uppercase text-[#737373] mt-5 block">
              Client name
            </label>
            <input
              type="text"
              value={clientName}
              onChange={(e) => setClientName(e.target.value)}
              placeholder="e.g. Kier Transportation"
              className="hl-input"
              data-testid="form-client"
            />
            <div className="text-[11px] text-[#525252] mt-1">
              Used exactly as entered. AI will never invent or change this.
            </div>

            <div className="grid grid-cols-1 gap-2 mt-6">
              <button
                type="button"
                onClick={() => analyse({ remix: false })}
                disabled={!canAnalyse}
                data-testid="analyze-button"
                className="hl-btn-primary w-full inline-flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Sparkles size={14} />
                {analysing && !aiDone
                  ? "Analysing image…"
                  : aiDone
                  ? "Re-run AI analysis"
                  : mode === "video"
                  ? "AI · Describe this video"
                  : "AI · Generate title & details"}
              </button>
              {aiDone && (
                <button
                  type="button"
                  onClick={() => analyse({ remix: true })}
                  disabled={!canAnalyse}
                  data-testid="remix-button"
                  className="hl-btn-secondary w-full inline-flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <RefreshCw size={14} />
                  {analysing ? "Remixing…" : "Remix · Fresh wording"}
                </button>
              )}
            </div>
            {!file && (
              <div className="text-[11px] text-[#525252] mt-2 text-center">
                {mode === "video" ? "Upload a poster image to enable AI." : "Drop an image to enable AI analysis."}
              </div>
            )}
            {file && !originalName.trim() && (
              <div className="text-[11px] text-[#525252] mt-2 text-center">
                Enter the original {mode === "video" ? "video" : "photo"} name to enable AI.
              </div>
            )}
          </div>

          {/* AI / editable output */}
          {mode === "video" && (
            <>
              <div className="text-[10px] tracking-[0.32em] uppercase text-[#4A6D8C] mt-4 mb-3">
                2 · Video source
              </div>
              <div className="flex gap-2 mb-2" data-testid="video-kind-picker">
                {VIDEO_KINDS.map((k) => (
                  <button
                    type="button"
                    key={k}
                    onClick={() => setForm((f) => ({ ...f, video_kind: k }))}
                    data-testid={`video-kind-${k}`}
                    className={`hl-chip ${form.video_kind === k ? "active" : ""}`}
                  >
                    {k === "mp4" ? "MP4" : k === "vimeo" ? "Vimeo" : "YouTube"}
                  </button>
                ))}
              </div>
              <input
                type="text"
                value={form.video}
                onChange={(e) => {
                  const v = e.target.value;
                  setForm((f) => ({
                    ...f,
                    video: v,
                    video_kind: /vimeo\.com/.test(v) ? "vimeo" : /youtu\.?be/.test(v) ? "youtube" : f.video_kind,
                  }));
                }}
                placeholder={
                  form.video_kind === "vimeo"
                    ? "https://vimeo.com/123456789"
                    : form.video_kind === "youtube"
                    ? "https://youtu.be/xxxxxxxxxxx"
                    : "https://…/your-clip.mp4"
                }
                className="hl-input"
                data-testid="form-video"
              />
              {form.video_kind !== "mp4" && (
                <button
                  type="button"
                  onClick={fetchVideoMeta}
                  disabled={fetchingMeta || !form.video.trim()}
                  data-testid="fetch-video-meta"
                  className="hl-btn-secondary mt-3 inline-flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {fetchingMeta ? <RefreshCw size={14} className="animate-spin" /> : <Download size={14} />}
                  {fetchingMeta
                    ? "Fetching from video…"
                    : `Fetch thumbnail & details from ${form.video_kind === "vimeo" ? "Vimeo" : "YouTube"}`}
                </button>
              )}
              <div className="text-[11px] text-[#525252] mt-2">
                No poster needed - the thumbnail, title and description are pulled straight from the
                video. It's also fetched automatically when you save. Upload an image only if you
                want a custom poster.
              </div>
            </>
          )}

          <div className="text-[10px] tracking-[0.32em] uppercase text-[#4A6D8C] mt-6 mb-3">
            {mode === "video" ? "3" : "2"} · AI output (editable)
          </div>

          <label className="text-[10px] tracking-[0.28em] uppercase text-[#737373]">
            AI title
          </label>
          <input
            type="text"
            value={form.title}
            onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
            placeholder="2 to 5 words, e.g. Night Works Setup"
            className="hl-input"
            data-testid="form-title"
          />

          {mode === "photo" && (
            <>
              <div className="text-[10px] tracking-[0.28em] uppercase text-[#737373] mt-5 mb-2 flex items-center justify-between">
                <span className="flex items-center gap-1.5">
                  {kitOptions.drones.includes(form.kit_used) ? <Plane size={11} /> : <Camera size={11} />}
                  Kit used
                </span>
                <button
                  type="button"
                  onClick={detectKitFromFile}
                  disabled={!file || detectingKit}
                  data-testid="detect-kit-button"
                  className="text-[10px] tracking-[0.22em] uppercase text-[#4A6D8C] hover:text-white disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1"
                >
                  <Sparkles size={11} /> {detectingKit ? "Detecting…" : "AI detect"}
                </button>
              </div>
              <select
                value={form.kit_used}
                onChange={(e) => setForm((f) => ({ ...f, kit_used: e.target.value }))}
                className="hl-input"
                data-testid="form-kit"
              >
                <optgroup label="Drones">
                  {kitOptions.drones.map((d) => <option key={d} value={d}>{d}</option>)}
                </optgroup>
                <optgroup label="Ground cameras">
                  {kitOptions.cameras.map((c) => <option key={c} value={c}>{c}</option>)}
                </optgroup>
                <option value="Other">Other / Unknown</option>
              </select>

              <div className="text-[10px] tracking-[0.28em] uppercase text-[#737373] mt-5 mb-2">
                Category
              </div>
              <ChipRow
                items={CATEGORIES}
                value={form.category}
                onChange={(c) => setForm((f) => ({ ...f, category: c }))}
                testidPrefix="cat"
              />
            </>
          )}

          <div className="text-[10px] tracking-[0.28em] uppercase text-[#737373] mt-5 mb-2">
            Tile size
          </div>
          <ChipRow
            items={SIZES}
            value={form.size}
            onChange={(s) => setForm((f) => ({ ...f, size: s }))}
            testidPrefix="size"
          />

          <label className="text-[10px] tracking-[0.28em] uppercase text-[#737373] mt-5">
            Alt text (SEO)
          </label>
          <input
            type="text"
            value={form.alt_text}
            onChange={(e) => setForm((f) => ({ ...f, alt_text: e.target.value }))}
            placeholder="8 to 18 words describing the image."
            className="hl-input"
            data-testid="form-alt"
          />

          <label className="text-[10px] tracking-[0.28em] uppercase text-[#737373] mt-4">
            Description
          </label>
          <textarea
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            placeholder="1 to 2 sentences describing what is visible."
            rows={4}
            className="hl-input"
            data-testid="form-description"
          />

          <label className="mt-6 inline-flex items-center gap-3 text-[12px] text-[#c9d1d9]">
            <input
              type="checkbox"
              checked={form.featured}
              onChange={(e) => setForm((f) => ({ ...f, featured: e.target.checked }))}
              data-testid="form-featured"
              className="w-4 h-4 accent-[#4A6D8C]"
            />
            Mark as featured (boosted to the top of the grid)
          </label>

          {mode === "photo" && (
            <label className="mt-3 inline-flex items-center gap-3 text-[12px] text-[#c9d1d9]">
              <input
                type="checkbox"
                checked={upscaleAfter}
                onChange={(e) => setUpscaleAfter(e.target.checked)}
                data-testid="form-upscale-after"
                className="w-4 h-4 accent-[#4A6D8C]"
              />
              <span className="inline-flex items-center gap-1.5">
                <Sparkles size={12} className="text-[#658CAE]" />
                AI-upscale to ultra-HD on save (adds 20–60s · works for drafts and published)
              </span>
            </label>
          )}

          <div className="flex flex-wrap gap-3 mt-10">
            <button
              type="button"
              onClick={() => save(false)}
              disabled={saving || preparingFile}
              data-testid="save-publish"
              className="hl-btn-primary disabled:opacity-60"
            >
              {saving ? "Saving…" : preparingFile ? "Preparing image…" : "Publish to gallery"}
            </button>
            <button
              type="button"
              onClick={() => save(true)}
              disabled={saving || preparingFile}
              data-testid="save-draft"
              className="hl-btn-ghost disabled:opacity-60"
            >
              Save as draft
            </button>
          </div>

          <div className="text-[11px] text-[#525252] mt-4">
            Nothing is published until you click Publish. All AI suggestions are editable.
          </div>
        </div>
      </div>
    </div>
  );
}
